/**
 * LeanIQ Offer Routines — infinite carousel (forward loop, no reverse).
 *
 * Track layout when infinite: [clones][originals][clones]
 * Scrolling into the clone set looks like the next/prev cards; we then
 * silently jump by one segment so motion never animates backward.
 */
(function () {
  function cards(track) {
    return Array.from(track.querySelectorAll('.liq-routines__card'));
  }

  function realCards(track) {
    return cards(track).filter(function (card) {
      return card.getAttribute('data-clone') !== 'true';
    });
  }

  function cardStep(track) {
    var list = cards(track);
    if (!list.length) return Math.max(1, track.clientWidth);
    var first = list[0];
    var second = list[1];
    if (second) return Math.max(1, Math.round(second.offsetLeft - first.offsetLeft));
    return Math.max(1, Math.round(first.getBoundingClientRect().width + 12));
  }

  function isInfinite(root) {
    return root.getAttribute('data-liq-routines-infinite') !== 'false';
  }

  function setNavDisabled(btn, disabled) {
    if (!btn) return;
    btn.disabled = disabled;
    btn.classList.toggle('is-disabled', disabled);
    btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  function updateNav(root, track) {
    var prev = root.querySelector('[data-liq-routines-prev]');
    var next = root.querySelector('[data-liq-routines-next]');
    if (isInfinite(root) && realCards(track).length > 1) {
      setNavDisabled(prev, false);
      setNavDisabled(next, false);
      return;
    }
    var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth - 2);
    var left = track.scrollLeft;
    setNavDisabled(prev, left <= 2);
    setNavDisabled(next, left >= maxScroll);
  }

  function currentIndex(track) {
    if (!cards(track).length) return 0;
    var trackRect = track.getBoundingClientRect();
    var best = cards(track)[0];
    var bestDist = Infinity;
    cards(track).forEach(function (card) {
      var rect = card.getBoundingClientRect();
      var dist = Math.abs(rect.left - trackRect.left);
      if (dist < bestDist) {
        bestDist = dist;
        best = card;
      }
    });
    return Number(best.getAttribute('data-index') || 0);
  }

  function updateDots(root, track) {
    var dots = root.querySelectorAll('[data-liq-routines-dot]');
    if (!dots.length) return;
    var index = currentIndex(track);
    var count = dots.length;
    var normalized = ((index % count) + count) % count;
    dots.forEach(function (dot, i) {
      var on = i === normalized;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-current', on ? 'true' : 'false');
    });
  }

  function sync(root, track) {
    updateNav(root, track);
    updateDots(root, track);
  }

  function cloneCard(card) {
    var clone = card.cloneNode(true);
    clone.setAttribute('data-clone', 'true');
    clone.setAttribute('aria-hidden', 'true');
    // Keep the same look as originals (same poster/sources); just don't autoplay.
    clone.querySelectorAll('video').forEach(function (video) {
      try {
        video.pause();
      } catch (err) {
        /* ignore */
      }
      video.muted = true;
      video.autoplay = false;
      video.preload = 'metadata';
    });
    return clone;
  }

  function measureSegmentWidth(track) {
    var firstReal = track.querySelector('.liq-routines__card:not([data-clone="true"])');
    if (!firstReal) return 0;
    var width = firstReal.offsetLeft;
    track.dataset.liqSegmentWidth = String(width);
    return width;
  }

  function setupInfiniteTrack(track) {
    if (track.dataset.liqInfiniteSetup === '1') return;
    var originals = realCards(track);
    if (originals.length < 2) return;

    track.dataset.liqInfiniteSetup = '1';

    originals.forEach(function (card) {
      track.appendChild(cloneCard(card));
    });

    for (var i = originals.length - 1; i >= 0; i -= 1) {
      track.insertBefore(cloneCard(originals[i]), track.firstChild);
    }
  }

  /**
   * Silent jump when we have scrolled into a clone segment.
   * Must run only after scrolling settles — never mid smooth-scroll.
   */
  function normalizeInfiniteScroll(track) {
    var width = Number(track.dataset.liqSegmentWidth || 0) || measureSegmentWidth(track);
    if (width <= 0) return false;

    var left = track.scrollLeft;
    var jumped = false;

    // Disable any inherited smooth behavior for the jump
    var prevBehavior = track.style.scrollBehavior;
    track.style.scrollBehavior = 'auto';

    if (left < width) {
      track.scrollLeft = left + width;
      jumped = true;
    } else if (left >= width * 2) {
      track.scrollLeft = left - width;
      jumped = true;
    }

    track.style.scrollBehavior = prevBehavior;
    return jumped;
  }

  function animateScrollBy(track, delta, onDone) {
    var start = track.scrollLeft;
    var target = start + delta;
    var startTime = null;
    var duration = 320;

    function frame(now) {
      if (startTime === null) startTime = now;
      var t = Math.min(1, (now - startTime) / duration);
      // ease-out cubic
      var eased = 1 - Math.pow(1 - t, 3);
      track.scrollLeft = start + (target - start) * eased;
      if (t < 1) {
        requestAnimationFrame(frame);
      } else {
        track.scrollLeft = target;
        if (onDone) onDone();
      }
    }

    requestAnimationFrame(frame);
  }

  function goNext(root, track, state) {
    if (state.animating) return;
    var step = cardStep(track);

    if (!isInfinite(root)) {
      track.scrollBy({ left: step, behavior: 'smooth' });
      return;
    }

    state.animating = true;
    animateScrollBy(track, step, function () {
      normalizeInfiniteScroll(track);
      state.animating = false;
      sync(root, track);
    });
  }

  function goPrev(root, track, state) {
    if (state.animating) return;
    var step = cardStep(track);

    if (!isInfinite(root)) {
      track.scrollBy({ left: -step, behavior: 'smooth' });
      return;
    }

    state.animating = true;
    animateScrollBy(track, -step, function () {
      normalizeInfiniteScroll(track);
      state.animating = false;
      sync(root, track);
    });
  }

  function scrollToIndex(root, track, index, state) {
    if (state.animating) return;
    var list = realCards(track);
    var target = null;
    for (var i = 0; i < list.length; i += 1) {
      if (Number(list[i].getAttribute('data-index') || 0) === index) {
        target = list[i];
        break;
      }
    }
    if (!target) return;

    var dest = target.offsetLeft;
    var delta = dest - track.scrollLeft;
    if (Math.abs(delta) < 2) return;

    if (!isInfinite(root)) {
      track.scrollTo({ left: dest, behavior: 'smooth' });
      return;
    }

    state.animating = true;
    animateScrollBy(track, delta, function () {
      normalizeInfiniteScroll(track);
      state.animating = false;
      sync(root, track);
    });
  }

  function init(root) {
    if (!root || root.dataset.liqRoutinesReady === '1') return;
    root.dataset.liqRoutinesReady = '1';

    var track = root.querySelector('[data-liq-routines-track]');
    if (!track) return;

    var prev = root.querySelector('[data-liq-routines-prev]');
    var next = root.querySelector('[data-liq-routines-next]');
    var dots = root.querySelectorAll('[data-liq-routines-dot]');
    var state = { animating: false, settleTimer: null };

    root.classList.toggle('is-infinite', isInfinite(root));

    if (isInfinite(root)) {
      setupInfiniteTrack(track);
    }

    if (prev) {
      prev.addEventListener('click', function () {
        goPrev(root, track, state);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        goNext(root, track, state);
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var index = Number(dot.getAttribute('data-liq-routines-dot') || 0);
        scrollToIndex(root, track, index, state);
      });
    });

    track.addEventListener(
      'scroll',
      function () {
        sync(root, track);
        if (!isInfinite(root) || state.animating) return;

        // After finger/wheel momentum ends, silently wrap if needed
        clearTimeout(state.settleTimer);
        state.settleTimer = setTimeout(function () {
          normalizeInfiniteScroll(track);
          sync(root, track);
        }, 80);
      },
      { passive: true }
    );

    track.addEventListener('scrollend', function () {
      if (!isInfinite(root) || state.animating) {
        sync(root, track);
        return;
      }
      normalizeInfiniteScroll(track);
      sync(root, track);
    });

    window.addEventListener(
      'resize',
      function () {
        if (isInfinite(root)) measureSegmentWidth(track);
        sync(root, track);
      },
      { passive: true }
    );

    var videos = track.querySelectorAll('.liq-routines__card:not([data-clone="true"]) video');
    var noDownload = root.getAttribute('data-liq-routines-nodownload') === 'true';
    videos.forEach(function (video) {
      if (noDownload) {
        var list = (video.getAttribute('controlslist') || '').split(/\s+/).filter(Boolean);
        if (list.indexOf('nodownload') === -1) list.push('nodownload');
        video.setAttribute('controlslist', list.join(' '));
      }

      video.addEventListener('play', function () {
        videos.forEach(function (other) {
          if (other !== video && !other.paused) other.pause();
        });
      });
    });

    // Position on the middle (real) segment after layout
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (isInfinite(root)) {
          var width = measureSegmentWidth(track);
          if (width > 0) track.scrollLeft = width;
        }
        sync(root, track);
      });
    });
  }

  function boot() {
    document.querySelectorAll('[data-liq-routines]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root =
      event.target && event.target.querySelector
        ? event.target.querySelector('[data-liq-routines]')
        : null;
    if (root) {
      root.dataset.liqRoutinesReady = '';
      var track = root.querySelector('[data-liq-routines-track]');
      if (track) {
        track.dataset.liqInfiniteSetup = '';
        track.dataset.liqSegmentWidth = '';
      }
      init(root);
    }
  });
})();
